import React, {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { isEventInsideElement } from "~/utils/isEventInsideElement";

interface IUseHoverDropdownParams {
  isMobile: boolean;
  isDesktop: boolean;
}

interface IUseHoverDropdownResult {
  anchorEl: HTMLElement | null;
  open: boolean;
  popperContentRef: React.RefObject<HTMLDivElement | null>;
  handleClose: () => void;
  handleTriggerClick: (event: ReactMouseEvent<HTMLElement>) => void;
  handleTriggerMouseEnter: (event: ReactMouseEvent<HTMLElement>) => void;
  handleTriggerMouseLeave: (event: ReactMouseEvent<HTMLElement>) => void;
  handlePopperMouseEnter: () => void;
  handlePopperMouseLeave: (event: ReactMouseEvent<HTMLElement>) => void;
}

export const useHoverDropdown = ({
  isMobile,
  isDesktop,
}: IUseHoverDropdownParams): IUseHoverDropdownResult => {
  const popperContentRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const clearCloseTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleOpen = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      clearCloseTimeout();
      setAnchorEl(event.currentTarget);
    },
    [clearCloseTimeout]
  );

  const handleClose = useCallback(() => {
    clearCloseTimeout();

    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
      timeoutRef.current = null;
    }, 200);
  }, [clearCloseTimeout]);

  const handlePopperMouseEnter = useCallback(() => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handleTriggerClick = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (!isMobile) return;

      clearCloseTimeout();
      setAnchorEl((prev) => (prev ? null : event.currentTarget));
    },
    [clearCloseTimeout, isMobile]
  );

  const handleTriggerMouseEnter = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (!isDesktop) return;

      handleOpen(event);
    },
    [handleOpen, isDesktop]
  );

  const handleTriggerMouseLeave = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (!isDesktop) return;

      if (isEventInsideElement(event, popperContentRef.current)) {
        return;
      }

      handleClose();
    },
    [handleClose, isDesktop]
  );

  const handlePopperMouseLeave = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (!isDesktop) return;

      if (isEventInsideElement(event, anchorEl)) {
        return;
      }

      handleClose();
    },
    [anchorEl, handleClose, isDesktop]
  );

  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, [clearCloseTimeout]);

  return {
    anchorEl,
    open,
    popperContentRef,
    handleClose,
    handleTriggerClick,
    handleTriggerMouseEnter,
    handleTriggerMouseLeave,
    handlePopperMouseEnter,
    handlePopperMouseLeave,
  };
};
